import { EXTERNAL_RULE_TYPE } from '../../utils/constants';
import HttpServiceSingleton from '../HttpService/HttpService';
import {
  type IDPConfig,
  type IDPConfigKosmos,
  type IDPECMConfig,
  type IDPECMConfigPayload,
  type Rule,
} from '../types';

import {
  APPLY_RULES_POST,
  DASHBOARD_URL,
  ECM_CONFIG_URL_POST,
  ECM_CONFIG_URL_PUT,
  IDP_CONFIG_POST,
  IDP_GENERATE_LINK_POST,
  IDP_PROVIDERS_URL_GET,
  IDP_VERIFICATION_POST,
  REQUESTABLE_USERS_URL_GET,
  RULE_DELETE,
  RULE_GET,
  RULE_UPDATE,
  RULE_URL_POST,
  RULES_METADATA_GET,
  RULES_URL_GET,
  USERS_URL,
  USERS_URL_GET,
} from './constants';

export interface UsersProps {
  q: string;
  enabled?: boolean;
  size?: number;
  userType?: string;
  searchKeyword?: string;
  sortOrder?: string;
  page?: number;
  sortBy?: string;
}

export interface RequestableUsersProps {
  requestor: string;
  tileType?: string;
  max?: number;
  offset?: number;
  requestableData?: string;
  sSearch?: string;
  enabled?: boolean;
  statuskey?: number;
  sort?: string;
  order?: string;
}

export interface FormsProps {
  category: number;
  searchText: string;
  formCategory: string;
  formTags: string;
  urlSlug: string;
  description: string;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: string;
  status: string[];
  createUser: string[];
  updateUser: string[];
}

const ExternalApiService = {
  getUsers(queryParams: UsersProps) {
    const stringifiedParams: Record<string, string> = Object.fromEntries(
      Object.entries(queryParams).map(([key, value]) => [key, String(value)])
    );
    const queryStr = new URLSearchParams(stringifiedParams).toString();

    return HttpServiceSingleton.get(`${USERS_URL}?${queryStr}`);
  },
  getRequestableUsers(queryParams: RequestableUsersProps) {
    const stringifiedParams: Record<string, string> = Object.fromEntries(
      Object.entries(queryParams).map(([key, value]) => [key, String(value)])
    );
    const queryStr = new URLSearchParams(stringifiedParams).toString();

    return HttpServiceSingleton.get(`${REQUESTABLE_USERS_URL_GET}&${queryStr}`);
  },
  postUsers(queryParams: UsersProps) {
    return HttpServiceSingleton.post(`${USERS_URL_GET}`, {
      searchKeyword: queryParams.searchKeyword,
      userType: queryParams.userType,
      size: queryParams.size,
      page: queryParams.page,
      sortOrder: queryParams.sortOrder,
      sortBy: queryParams.sortBy,
    });
  },
  postForms(queryParams: FormsProps) {
    return HttpServiceSingleton.post('/ECMv6/api/ext/forms/submit/search', {
      category: queryParams.category,
      searchText: queryParams.searchText,
      formCategory: queryParams.formCategory,
      formTags: queryParams.formTags,
      urlSlug: queryParams.urlSlug,
      description: queryParams.description,
      page: queryParams.page,
      size: queryParams.size,
      sortBy: queryParams.sortBy,
      sortOrder: queryParams.sortOrder,
      status: queryParams.status,
      createUser: queryParams.createUser,
      updateUser: queryParams.updateUser,
    });
  },
  getDashboardDetails() {
    return HttpServiceSingleton.get(DASHBOARD_URL);
  },
  getRules() {
    return HttpServiceSingleton.post(RULES_URL_GET, {
      type: EXTERNAL_RULE_TYPE,
      size: 100,
    });
  },
  getRulesMetadata() {
    return HttpServiceSingleton.get(RULES_METADATA_GET);
  },
  getRuleSampleUsers(rulekey: number) {
    return HttpServiceSingleton.get(`${RULE_GET}/${rulekey}/preview`);
  },
  getIDPURL(key: string) {
    return HttpServiceSingleton.post(IDP_VERIFICATION_POST, {
      sessionUUID: key,
    });
  },
  generateIDPLink(key: string) {
    return HttpServiceSingleton.post(IDP_GENERATE_LINK_POST, {
      sessionUUID: key,
    });
  },
  postECMConfig(config: IDPECMConfig) {
    return HttpServiceSingleton.post(`${ECM_CONFIG_URL_POST}`, config);
  },
  putECMConfig(config: IDPECMConfigPayload) {
    return HttpServiceSingleton.put(`${ECM_CONFIG_URL_PUT}`, config);
  },
  postIDPConfig(vendor: IDPConfig | IDPConfigKosmos) {
    return HttpServiceSingleton.post(`${IDP_CONFIG_POST}`, vendor);
  },
  putIDPConfig(vendor: IDPConfig | IDPConfigKosmos) {
    return HttpServiceSingleton.put(`${IDP_CONFIG_POST}`, vendor);
  },
  updateRule(rule: Rule) {
    return HttpServiceSingleton.put(
      `${RULE_UPDATE}/${rule.ruleKey ?? 0}`,
      rule
    );
  },
  createRule(rule: Rule) {
    return HttpServiceSingleton.post(`${RULE_URL_POST}`, rule);
  },
  deleteRule(rule: Rule) {
    return HttpServiceSingleton.delete(`${RULE_DELETE}/${rule.ruleKey ?? 0}`);
  },
  getIDProofingProviders() {
    return HttpServiceSingleton.post(IDP_PROVIDERS_URL_GET, {});
  },
  postApplyRules(str: string) {
    return HttpServiceSingleton.post(`${APPLY_RULES_POST}${str}`, {});
  },
};

export default ExternalApiService;
